import {
    AdminLandingPageLink,
    buildOptionKeyMap,
    findByShopifyId,
    primaryProductVariantsAndOptions,
    resolveDefaultVariantSource,
    selectClosestAvailableVariant,
    variantDisplayLabelOrId,
    type DefaultVariantSource,
} from '@storefronts/common-types'

/** Narrows an AdminLandingPageLink to primary-product variants and options. */
function primaryProductFilter(link: AdminLandingPageLink) {
    return primaryProductVariantsAndOptions({
        variants: link.variantInfo ?? [],
        options: link.optionInfo ?? [],
        virtualProductId: link.virtualProductId,
        productGroupKey: link.productGroupKey,
        shopifyId: link.shopifyId,
    })
}

/** Variant count relevant for the default-variant UI (primary-product only for VPs). */
function selectableVariantCount(link: AdminLandingPageLink): number {
    return primaryProductFilter(link).variants.length
}

/**
 * Returns the effective variant ID for a link, considering override, explicit,
 * best-selling, rendered default, and first variant in that order.
 */
export function getEffectiveVariantIdForLink(
    link: AdminLandingPageLink,
): string | null {
    const { variantId } = resolveDefaultVariantSource({
        defaultVariantOverrideId: link.defaultVariantOverrideId,
        defaultVariantId: link.defaultVariantId,
        bestSellingVariantId: link.bestSellingVariantId,
        fallbackVariantId:
            link.renderedDefaultVariantId ??
            link.variantInfo?.[0]?.shopifyId ??
            null,
    })
    return variantId
}

/**
 * Returns the source and variant ID for a link, excluding storefront override.
 * Used to determine what the "product level default" is.
 */
function resolveProductLevelDefault(link: AdminLandingPageLink): {
    source: DefaultVariantSource | null
    variantId: string | null
} {
    const { variants } = primaryProductFilter(link)
    return resolveDefaultVariantSource({
        defaultVariantId: link.defaultVariantId,
        bestSellingVariantId: link.bestSellingVariantId,
        fallbackVariantId:
            firstAvailableOrRenderedVariantId(link) ??
            variants[0]?.shopifyId ??
            null,
    })
}

/** First in-stock primary-product variant id, else rendered default id. */
function firstAvailableOrRenderedVariantId(
    link: AdminLandingPageLink,
): string | null {
    const { variants, options } = primaryProductFilter(link)
    const firstAvailable = selectClosestAvailableVariant({
        variants,
        options,
        targetVariant: null,
    })
    return firstAvailable?.shopifyId ?? link.renderedDefaultVariantId ?? null
}

export function defaultVariantLabelForLink(
    link: AdminLandingPageLink,
    variantId: string,
): string {
    const variantInfo = link.variantInfo ?? []
    const { options: filteredOptions } = primaryProductFilter(link)
    return variantDisplayLabelOrId(
        findByShopifyId(variantInfo, variantId),
        filteredOptions,
        buildOptionKeyMap(filteredOptions),
        variantId,
    )
}

/** Return shape for {@link getProductPageDefaultVariantSummaryParts} (used by the selection panel UI). */
export type ProductPageVariantSummaryParts =
    | { lead: string; variantLabel: string }
    | { text: string }

/** Source types for product-level defaults (no storefront override). */
type ProductLevelSource = Exclude<DefaultVariantSource, 'override'>

/** Maps source types to their display labels for product-page summary. */
const PRODUCT_PAGE_LEAD_BY_SOURCE: Record<ProductLevelSource, string> = {
    explicit: 'Currently ',
    bestSelling: 'Best selling variant - Currently ',
    fallback: 'First available variant - Currently ',
}

/**
 * Product-page default summary for display (ignores storefront override).
 * Uses centralized resolveDefaultVariantSource for consistent fallback logic.
 */
export function getProductPageDefaultVariantSummaryParts(
    admin: AdminLandingPageLink,
): ProductPageVariantSummaryParts {
    const { source, variantId } = resolveProductLevelDefault(admin)

    if (!source || !variantId) {
        return {
            text: 'Currently unset. First available variant will be used.',
        }
    }

    // Since resolveProductLevelDefault doesn't include override, source is ProductLevelSource
    return {
        lead: PRODUCT_PAGE_LEAD_BY_SOURCE[source as ProductLevelSource],
        variantLabel: defaultVariantLabelForLink(admin, variantId),
    }
}

function linkHasAnyDefaultVariantField(link: AdminLandingPageLink): boolean {
    const { source } = resolveDefaultVariantSource({
        defaultVariantOverrideId: link.defaultVariantOverrideId,
        defaultVariantId: link.defaultVariantId,
        bestSellingVariantId: link.bestSellingVariantId,
        fallbackVariantId: link.renderedDefaultVariantId,
    })
    return source !== null
}

/**
 * Whether the selected-product row should reserve the default-variant line (multi-variant
 * product with at least one default signal). When true, {@link getSelectedProductDefaultVariantCaption}
 * returns a non-null caption.
 */
export function shouldShowSelectedProductDefaultVariantLine(
    link: AdminLandingPageLink,
): boolean {
    if (selectableVariantCount(link) <= 1) return false
    return linkHasAnyDefaultVariantField(link)
}

/** Maps source types to their display prefixes for the selected-product caption. */
const CAPTION_PREFIX_BY_SOURCE: Record<DefaultVariantSource, string> = {
    override: 'Default Variant: ',
    explicit: 'Default Variant: Use product level default - Currently ',
    bestSelling:
        'Default Variant: Use product best selling variant - Currently ',
    fallback: 'Default Variant: First available variant - Currently ',
}

/**
 * Copy for the selected-product default-variant line; null when
 * {@link shouldShowSelectedProductDefaultVariantLine} is false.
 * Uses centralized resolveDefaultVariantSource for consistent fallback logic.
 */
export function getSelectedProductDefaultVariantCaption(
    link: AdminLandingPageLink,
): { prefix: string; label: string; emphasized: boolean } | null {
    if (selectableVariantCount(link) <= 1) return null
    if (!linkHasAnyDefaultVariantField(link)) return null

    const overrideHonored =
        !!link.defaultVariantOverrideId &&
        (link.renderedDefaultVariantId == null ||
            link.renderedDefaultVariantId === link.defaultVariantOverrideId)
    if (overrideHonored && link.defaultVariantOverrideId) {
        return {
            prefix: CAPTION_PREFIX_BY_SOURCE.override,
            label: defaultVariantLabelForLink(
                link,
                link.defaultVariantOverrideId,
            ),
            emphasized: true,
        }
    }

    // For product-level defaults, use the resolved source
    const { source, variantId } = resolveProductLevelDefault(link)
    if (!source || !variantId) return null

    return {
        prefix: CAPTION_PREFIX_BY_SOURCE[source],
        label: defaultVariantLabelForLink(link, variantId),
        emphasized: false,
    }
}
